import React from 'react';
import { Progress, Tooltip, Spin } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { DEFAULT_DATE_TIME } from '../../../../utils/constants/timeFormats';
import { ElapsedSpan } from '../../../../components/Vehicle/Data/DateTime.styled';
import Elapsed from '../../../../components/Vehicle/Data/Elapsed';

const driverInfoKey = 'VEHICLE.DRIVER_INFO';
const driverInfoFieldKey = `${driverInfoKey}.FIELD`;

const formatDurationInSeconds = (duration, defaultValue = '-') => {
  if (!duration && duration !== 0) return defaultValue;

  let result = '';
  const totalMinutes = Math.floor(duration / 60);
  const totalHours = Math.floor(totalMinutes / 60);

  const remainingMinutes = totalMinutes % 60;

  if (totalHours > 0) {
    result += `${totalHours}h `;
  }

  if (remainingMinutes > 0) {
    result += `${remainingMinutes}min`;
  }

  return result.trim() || '0min';
};

const renderCalculationSource = (source, t) => {
  if (source === 'Virtual Tachograph') {
    return (
      <Tooltip title={t('TACHOGRAPH.VIRTUAL.TOOLTIP_INFO')}>
        {`${source} `}
        <InfoCircleOutlined />
      </Tooltip>
    );
  }
  return source;
};

const renderDefaultDriveCapacityValue = (driver, limitKey, t) => {
  if (!driver.hasDrivingLimits) {
    return t(`${driverInfoKey}.NO_LIMIT`);
  }
  if (driver.dataSource === 'Virtual Tachograph') {
    return (
      <Tooltip title={t('TACHOGRAPH.VIRTUAL.UNKNOWN.TOOLTIP_INFO')}>
        {`${t(`${driverInfoKey}.UNKNOWN`)} `}
        <InfoCircleOutlined />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={t(`${limitKey}.UNKNOWN.TOOLTIP_INFO`)}>
      {`${t(`${driverInfoKey}.UNKNOWN`)} `}
      <InfoCircleOutlined />
    </Tooltip>
  );
};

const renderAwaitingDataDriveCapacity = (t) => (
  <Tooltip title={t(`${driverInfoKey}.AWAITING_DATA.TOOLTIP_INFO`)}>
    <Spin size="small" style={{ marginRight: '8px' }} />
    {`${t(`${driverInfoKey}.AWAITING_DATA.LABEL`)} `}
    <InfoCircleOutlined />
  </Tooltip>
);

const renderDriveCapacity = (driveCapacity, driver, limitKey, t, alternativeLimit) => {
  if (!driveCapacity || typeof driveCapacity !== 'object') {
    return formatDurationInSeconds(driveCapacity, renderDefaultDriveCapacityValue(driver, limitKey, t));
  }

  // The provider value was suppressed as untrustworthy (a tachograph break-reset lag) — show an
  // "awaiting data" state rather than a stale or empty number.
  if (driveCapacity.status === 'AWAITING_DATA') {
    return renderAwaitingDataDriveCapacity(t);
  }

  const { remaining, limit, remainingInPercent } = driveCapacity;

  return (
    <div>
      <Progress
        percent={remainingInPercent}
        type="circle"
        style={{ marginRight: '14px' }}
        width={80}
        strokeColor={{ '0%': '#121A1B', '100%': '#EAFCC5' }}
        strokeWidth={10}
        strokeLinecap="butt"
        trailColor="#f8f8f8"
        format={(percent) => `${percent} %`}
      />
      {`${formatDurationInSeconds(remaining)} `}
      <span style={{ opacity: 0.6 }}>
        {`(of ${alternativeLimit
          ? `${formatDurationInSeconds(limit)} / ${alternativeLimit}`
          : formatDurationInSeconds(limit)})`}
      </span>
    </div>
  );
};

const renderLastUpdate = (lastUpdate, t) => {
  if (!lastUpdate) {
    return t('COMMON.MISSING');
  }
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          flexDirection: 'column',
        }}
      >
        <div>{moment(lastUpdate.actualTime).format(DEFAULT_DATE_TIME)}</div>
        <ElapsedSpan layout="vertical">
          {moment.duration(moment(lastUpdate.actualTime).diff(moment())).humanize(true)}
        </ElapsedSpan>
      </div>
      <span style={{ fontSize: '10px', color: 'gray' }}>
        <Elapsed value={lastUpdate.checkTime} prefix={`${t('PROPERTIES.LAST_CHECK')} `} />
      </span>
    </div>
  );
};

const getDriverInfoConfig = (vehicle, t, drivingTimesState = {}) => {
  // Use data from the drivingTimes endpoint if available, otherwise fallback to vehicle data or defaults
  const drivingTimesData = drivingTimesState?.data?.drivingTimes || [];
  const isLoading = drivingTimesState?.loading || false;

  // Use all drivers in the list
  const driverInfoList = drivingTimesData.length > 0 && !isLoading
    ? drivingTimesData.map((drivingTime) => ({
      dataSource: drivingTime?.source?.value,
      workingState: drivingTime?.workingState,
      lastUpdate: drivingTime?.lastUpdate,
      hasDrivingLimits: drivingTime.hasDrivingLimits,
      nextDriveCapacity: drivingTime?.nextDriveCapacity,
      dailyDriveCapacity: drivingTime?.dailyDriveCapacity,
      weeklyDriveCapacity: drivingTime?.weeklyDriveCapacity,
    }))
    : [];

  // Create a configuration for each driver
  return driverInfoList.map((driver, index) => {
    const driverId = drivingTimesData[index]?.driverId || `Driver ${index + 1}`;
    const shortLimitKey = `${driverInfoFieldKey}.REMAINING_TIME_TO_MANDATORY_BREAK`;
    const dailyLimitKey = `${driverInfoFieldKey}.REMAINING_DAILY_DRIVING_TIME`;
    const weeklyLimitKey = `${driverInfoFieldKey}.REMAINING_WEEKLY_DRIVING_TIME`;

    return {
      driverId,
      config: [
        {
          title: `${driverInfoFieldKey}.DATA_SOURCE.NAME`,
          loading: () => !vehicle || isLoading,
          renderDetail: () => renderCalculationSource(driver.dataSource, t),
        },
        {
          title: `${driverInfoFieldKey}.WORKING_STATE.NAME`,
          loading: () => !vehicle || isLoading,
          renderDetail: () => t(`${driverInfoFieldKey}.WORKING_STATE.${driver.workingState}`),
        },
        {
          title: `${driverInfoFieldKey}.LAST_UPDATE`,
          loading: () => !vehicle || isLoading,
          renderDetail: () => renderLastUpdate(driver.lastUpdate, t),
        },
        {
          title: `${shortLimitKey}.NAME`,
          loading: () => !vehicle || isLoading,
          renderDetail: () => renderDriveCapacity(driver.nextDriveCapacity, driver, shortLimitKey, t),
        },
        {
          title: `${dailyLimitKey}.NAME`,
          loading: () => !vehicle || isLoading,
          renderDetail: () => renderDriveCapacity(driver.dailyDriveCapacity, driver, dailyLimitKey, t, '11h'),
        },
        {
          title: `${weeklyLimitKey}.NAME`,
          loading: () => !vehicle || isLoading,
          renderDetail: () => renderDriveCapacity(driver.weeklyDriveCapacity, driver, weeklyLimitKey, t),
        },
      ],
    };
  });
};

export default getDriverInfoConfig;
